import { useCallback, useEffect, useRef, useState } from 'react';
import aiAutomationApi from '../../../utils/aiAutomationApi';

export const isEvaluationRunning = (result) => ['queued', 'running'].includes(
  String(result?.evaluation_status || result?.refresh_status || '').toLowerCase(),
);

const empty = { result: null, loading: true, recalculating: false, error: '', waiting: false, checking: false };
const POLL_MS = 2500;
const WAIT_MS = 120000;
const WAIT_POLL_MS = 10000;

// Every report entry point uses the scheduled interview ID, never a candidate ID.
export default function useInterviewResult(open, interviewId) {
  const key = interviewId ? String(interviewId) : '';
  const [state, setState] = useState({ ...empty, key: '' });
  const context = useRef(null);

  const begin = useCallback((refresh = false) => {
    const current = context.current;
    if (!current || current.inFlight) return;
    clearTimeout(current.poll);
    clearTimeout(current.deadline);
    const cycle = ++current.cycle;
    current.expired = false;
    setState((previous) => ({ ...previous, error: '', waiting: false, checking: !refresh,
      recalculating: refresh, loading: !previous.result }));
    current.deadline = setTimeout(() => {
      if (context.current !== current) return;
      current.expired = true;
      setState((previous) => ({ ...previous, loading: false, recalculating: false, checking: false, waiting: true }));
    }, WAIT_MS);

    const request = async (recalculate = false) => {
      if (context.current !== current || current.cycle !== cycle || current.inFlight) return;
      current.inFlight = true;
      try {
        const query = recalculate ? '?refresh=true&recalculation_basis=saved_interview' : '';
        const data = await aiAutomationApi.get(`/interviews/${encodeURIComponent(current.key)}/ai-result${query}`);
        if (context.current !== current || current.cycle !== cycle) return;
        if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('The interview result could not be retrieved. Check status to retry.');
        if (data.scheduled_interview_id != null && String(data.scheduled_interview_id) !== current.key) {
          throw new Error('The returned report does not match the selected interview. This result needs review.');
        }
        const running = isEvaluationRunning(data);
        const failed = String(data?.evaluation_status || data?.refresh_status || '').toLowerCase() === 'failed';
        setState((previous) => ({ ...previous, result: data, loading: false, checking: false,
          recalculating: previous.recalculating && running && !current.expired,
          waiting: running && current.expired,
          error: failed ? (data.refresh_error || 'Scoring failed. Recalculate to retry; any previous result is retained.') : '' }));
        if (running) {
          current.poll = setTimeout(() => request(), current.expired ? WAIT_POLL_MS : POLL_MS);
        } else {
          current.expired = false;
          clearTimeout(current.deadline);
        }
      } catch (error) {
        if (context.current !== current || current.cycle !== cycle) return;
        clearTimeout(current.deadline);
        const detail = error?.response?.data?.detail || error?.detail;
        setState((previous) => ({ ...previous, loading: false, checking: false, recalculating: false,
          error: typeof detail === 'string' ? detail : (error?.message || 'Could not load this interview result.') }));
      } finally {
        if (current.cycle === cycle) current.inFlight = false;
      }
    };
    request(refresh);
  }, []);

  useEffect(() => {
    if (!open || !key) return undefined;
    const current = { key, inFlight: false, expired: false, cycle: 0 };
    context.current = current;
    setState((previous) => ({ ...empty, key, result: previous.key === key ? previous.result : null }));
    begin();
    return () => {
      if (context.current === current) context.current = null;
      clearTimeout(current.poll);
      clearTimeout(current.deadline);
    };
  }, [open, key, begin]);

  return { ...(state.key === key ? state : empty),
    checkStatus: useCallback(() => begin(false), [begin]),
    recalculate: useCallback(() => begin(true), [begin]) };
}
